/**
 * g19-equivalent Deposit helpers. Non-throwing. Do not change URL/account rules.
 */

export function asArr(v) {
  return Array.isArray(v) ? v : [];
}

/** Display-only. Uses backend DTO `badge`; does not recompute bonus. */
export function visiblePromotionBadge(promo) {
  try {
    const badge = String(promo && promo.badge != null ? promo.badge : '').trim();
    if (!badge) return '';
    const compact = badge.replace(/\s+/g, '').toLowerCase();
    if (
      compact === '0%' ||
      compact === '৳0' ||
      compact === 'tk0' ||
      compact === 'bdt0' ||
      compact === 'n/a' ||
      compact === 'na' ||
      compact === 'nobonus'
    ) {
      return '';
    }
    return badge;
  } catch {
    return '';
  }
}

/** Display-only mask. Never mutates source accountNumber. */
export function maskReceivingNumber(value) {
  try {
    const s = String(value == null ? '' : value);
    if (!s) return '';
    if (s.length >= 5) return `${s.slice(0, 5)}****${s.slice(-2)}`;
    if (s.length <= 2) return '****';
    return `${s.slice(0, 1)}****${s.slice(-1)}`;
  } catch {
    return '****';
  }
}

function defaultApiBase() {
  return '/api/site/WS0007/v1';
}

export function paymentAssetUrl(path, getApiBase) {
  try {
    if (!path) return '';
    const raw = String(path);
    if (/^https?:\/\//i.test(raw)) return raw;
    if (raw.startsWith('/payments/')) return raw;
    let p = raw;
    if (p.startsWith('/api/uploads/')) p = `/uploads/${p.slice('/api/uploads/'.length)}`;
    if (p.startsWith('/uploads/uploads/')) p = `/uploads/${p.slice('/uploads/uploads/'.length)}`;
    if (!p.startsWith('/uploads/')) return raw;
    let api = '';
    try {
      if (typeof getApiBase === 'function') api = String(getApiBase() || '');
      else api = String(defaultApiBase());
    } catch {
      api = '';
    }
    try {
      const abs = new URL(
        api,
        /^https?:\/\//i.test(api)
          ? undefined
          : typeof location !== 'undefined' && location.href
            ? location.href
            : 'http://127.0.0.1/'
      );
      if (/^https?:\/\//i.test(api)) return abs.origin + p;
      return abs.origin + '/api' + p;
    } catch {
      return '/api' + p;
    }
  } catch {
    try {
      return path ? String(path) : '';
    } catch {
      return '';
    }
  }
}

export function flattenDepositAccounts(row) {
  try {
    if (!row || typeof row !== 'object') return [];
    if (Array.isArray(row.accountTypes) && row.accountTypes.length) {
      return row.accountTypes.flatMap((g) => asArr(g && g.accounts));
    }
    return asArr(row.accounts);
  } catch {
    return [];
  }
}

export function depositAccountTypes(row) {
  try {
    if (!row || typeof row !== 'object') return [];
    if (Array.isArray(row.accountTypes) && row.accountTypes.length) {
      return row.accountTypes.filter((g) => g && asArr(g.accounts).length);
    }
    const acc = asArr(row.accounts);
    const map = new Map();
    acc.forEach((a) => {
      if (!a) return;
      const t = a.accountType || 'Personal';
      if (!map.has(t)) map.set(t, []);
      map.get(t).push(a);
    });
    return [...map.entries()].map(([type, accounts]) => ({ type, accounts }));
  } catch {
    return [];
  }
}

const CATALOG = [
  { id: 'bkash', name: 'bKash', logo: '/payments/bkash.svg' },
  { id: 'nagad', name: 'Nagad', logo: '/payments/nagad.svg' },
  { id: 'rocket', name: 'Rocket', logo: '/payments/rocket.svg' },
];

export function projectDepositRender(rows, { selectedId = '', getApiBase } = {}) {
  const managedRows = asArr(rows).filter((row) => row && row.source !== 'config');
  const list = managedRows.map((row) => {
        row = row || {};
        const cat = CATALOG.find((e) => e.id === row.code) || {
          id: row.code,
          name: row.displayName || row.code,
          logo: '',
        };
        const types = depositAccountTypes(row);
        return {
          id: row.code,
          name: row.displayName || cat.name,
          tileName: String(row.displayName || '').trim(),
          logo: paymentAssetUrl(row.icon, getApiBase) || cat.logo,
          managedId: row.id || '',
          accountTypes: types,
          accounts: flattenDepositAccounts(row),
          source: row.source || (row.id ? 'managed' : 'config'),
        };
      });

  const M = list.find((e) => e.id === selectedId) || list[0] || {};
  return { list, M };
}

/** UI-only. Mirrors existing Player amount bounds (min/max) + withdrawable; does not change wallet math. */
export function withdrawSubmitEnabled({
  submitting = false,
  walletReady = false,
  withdrawEnabled = true,
  methodId = '',
  methodsAvailable = 0,
  phoneVerified = false,
  accountNo = '',
  amount,
  minWithdraw,
  maxWithdraw,
  withdrawable,
  turnoverBlocked = false,
  accountDigitLength = 11,
} = {}) {
  if (submitting || !walletReady || withdrawEnabled === false) return false;
  if (!Number(methodsAvailable) || !String(methodId || '').trim()) return false;
  if (!phoneVerified) return false;
  const digits = String(accountNo || '').replace(/\D/g, '');
  if (digits.length !== Number(accountDigitLength)) return false;
  const n = Number(amount);
  const min = Number(minWithdraw);
  const max = Number(maxWithdraw);
  const wd = Number(withdrawable);
  if (!Number.isFinite(n) || n <= 0) return false;
  if (!Number.isFinite(min) || n < min) return false;
  if (!Number.isFinite(max) || n > max) return false;
  if (!Number.isFinite(wd) || n > wd) return false;
  if (turnoverBlocked === true) return false;
  return true;
}

export function isTurnoverWithdrawBlocked(wallet) {
  return wallet?.turnoverBlocked === true;
}

export const TURNOVER_NOTICE_MESSAGE = 'Please complete your turnover.';
export const TURNOVER_NOTICE_CLOSE_LABEL = 'Close turnover notice';

/** Safe popup visibility: never open from render, and never reopen after X on the same mount. */
export function nextTurnoverNoticeState({ turnoverBlocked, dismissed } = {}) {
  if (turnoverBlocked !== true) {
    return { open: false, dismissed: false };
  }
  if (dismissed === true) {
    return { open: false, dismissed: true };
  }
  return { open: true, dismissed: false };
}

export function projectWithdrawRender(rows, { selectedId = '', getApiBase } = {}) {
  const list = asArr(rows)
    .map((row) => {
      const rec = row || {};
      const code = String(rec.code || rec.id || '').trim().toLowerCase();
      if (!code) return null;
      return {
        id: code,
        name: rec.displayName || rec.name || code,
        logo: paymentAssetUrl(rec.icon, getApiBase) || `/payments/${code}.svg`,
        managedId: rec.id || '',
      };
    })
    .filter(Boolean);
  const M = list.find((e) => e.id === selectedId) || list[0] || null;
  return { list, M };
}

export function depositChannelVisible(M) {
  return (M && M.source === 'managed') || asArr(M && M.accountTypes).length;
}

export function depositRecv(M, rid) {
  return asArr(M && M.accounts).find((e) => e && e.id === rid);
}

/** Active receiving accounts for the selected payment-method channel. */
export function accountsForDepositChannel(M, Atp) {
  const selectedType = asArr(M && M.accountTypes).find((g) => g && g.type === Atp) || {};
  return asArr(selectedType.accounts).filter((a) => a && a.id);
}

/** Keep the current id when it is still in the list. Otherwise the first account. */
export function resolveInitialReceivingAccountId(accounts, currentId) {
  const list = asArr(accounts).filter((a) => a && a.id);
  if (!list.length) return '';
  const cur = String(currentId || '');
  if (cur && list.some((a) => a.id === cur)) return cur;
  return list[0].id;
}

/** Method change selects that method's first channel and first receiving account. */
export function selectionAfterMethodChange(method) {
  const types = asArr(method && method.accountTypes).filter((g) => g && g.type);
  const channel = types.length ? types[0].type : '';
  const accounts = accountsForDepositChannel(method, channel);
  return {
    channel,
    accountId: resolveInitialReceivingAccountId(accounts, ''),
  };
}

export function depositQrUrl(Recv, getApiBase) {
  return Recv && Recv.image ? paymentAssetUrl(Recv.image, getApiBase) || '' : '';
}

export function normalizeDepositTrxId(value) {
  return String(value || '').trim();
}

export const DEPOSIT_SUCCESS_POPUP_MS = 3000;
export const TRX_CHECK_DEBOUNCE_MS = 400;
export const DEPOSIT_UNAVAILABLE_MESSAGE = 'কাস্টমার প্রতিনিধির সাথে যোগাযোগ করুন।';
export const DEPOSIT_UNAVAILABLE_CLOSE_LABEL = 'Close';

function playerApiBase() {
  return '/api/site/WS0007/v1';
}

function playerAccessToken() {
  try {
    const raw = localStorage.getItem('ninet6_player_session');
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed?.accessToken || '';
  } catch {
    return '';
  }
}

function playerAuthHeaders() {
  const token = playerAccessToken();
  return {
    Accept: 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

export async function fetchDepositAvailability() {
  try {
    const res = await fetch(`${playerApiBase()}/deposits/availability`, {
      headers: playerAuthHeaders(),
      cache: 'no-store',
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      return { depositAvailable: false };
    }
    const data = json.data && typeof json.data === 'object' ? json.data : json;
    if (Object.prototype.hasOwnProperty.call(data, 'depositAvailable')) {
      return { depositAvailable: data.depositAvailable === true };
    }
    if (Object.prototype.hasOwnProperty.call(json, 'depositAvailable')) {
      return { depositAvailable: json.depositAvailable === true };
    }
    return { depositAvailable: false };
  } catch {
    return { depositAvailable: false };
  }
}

export async function checkDepositTrxId(trxId) {
  const id = normalizeDepositTrxId(trxId);
  if (!id) return { available: true };
  const params = new URLSearchParams({ trxId: id });
  const res = await fetch(`${playerApiBase()}/deposits/check-trx-id?${params}`, {
    headers: playerAuthHeaders(),
    cache: 'no-store',
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(json.message || json.error || `Request failed (${res.status})`);
    err.status = res.status;
    throw err;
  }
  return {
    available: json.available !== false,
    message: json.message || '',
  };
}

export function simulateDepositRender(rows, { selectedId = '', Atp = '', Rid = '', getApiBase } = {}) {
  const { list, M } = projectDepositRender(rows, { selectedId, getApiBase });
  const Recv = depositRecv(M, Rid);
  const showChannel = depositChannelVisible(M);
  const typeBtns = asArr(M.accountTypes).map((g) => g && g.type);
  const selectedType = asArr(M.accountTypes).find((g) => g && g.type === Atp) || {};
  const multi = Atp && asArr(selectedType.accounts).length > 1;
  const accBtns = multi ? asArr(selectedType.accounts).map((a) => a && a.id) : [];
  const qr = depositQrUrl(Recv, getApiBase);
  const txn = Recv ? String(Recv.transactionType || '').toUpperCase() : '';
  const maskedNumber = Recv ? maskReceivingNumber(Recv.accountNumber) : '';
  return { list, M, Recv, showChannel, typeBtns, multi, accBtns, qr, txn, maskedNumber };
}
